import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import WhatsAppCTA from '../components/WhatsAppCTA';
import OrnateDivider from '../components/OrnateDivider';
import EnrollBlock from '../components/EnrollBlock';
import EditableText from '../components/EditableText';
import SEO from '../components/SEO';

const Section = ({ label, title, children }) => (
  <section className="mb-16">
    <OrnateDivider label={label} variant="diamond" className="mb-6 max-w-xl mx-auto" />
    <h2 className="text-3xl md:text-4xl font-heading font-bold golden-text text-center uppercase tracking-widest mb-8">
      {title}
    </h2>
    <div className="card-mystical rounded-2xl p-8 md:p-10 text-lg leading-relaxed text-primary/85 space-y-5">
      {children}
    </div>
  </section>
);

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen mystical-bg pt-28 pb-20 relative">
      <SEO
        title="The Siddha Method — About · MyShaktiverse"
        description="A structured approach to working with your own consciousness, drawn from the living tradition of the Himalayan Siddhas."
        path="/about"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          {/* Hero — Section 1 */}
          <div className="text-center mb-14">
            <OrnateDivider label="About" variant="lotus" className="mb-6 max-w-xl mx-auto" />
            <h1 className="text-4xl md:text-6xl font-heading font-bold golden-text text-glow uppercase tracking-widest mb-6">
              <EditableText id="about.hero.title" defaultValue="The Siddha Method" />
            </h1>
            <p className="text-lg text-primary/85 leading-relaxed max-w-2xl mx-auto">
              <EditableText
                id="about.hero.subtitle"
                defaultValue="The Siddha Method is a structured approach to working with your own consciousness, drawn from the living tradition of the Himalayan Siddhas."
                multiline
              />
            </p>
          </div>

          {/* Opening */}
          <div className="card-mystical rounded-2xl p-8 md:p-10 mb-14 space-y-5 text-lg leading-relaxed text-primary/85">
            <p>
              <EditableText
                id="about.opening.p1"
                defaultValue="It is a set of precise, sequential practices that work directly on the human system: body, breath, and attention, creating the conditions in which awareness can stabilise, deepen, and ultimately recognise itself."
                multiline
              />
            </p>
            <p>
              <EditableText
                id="about.opening.p2"
                defaultValue="The method asks one thing: sincerity and consistency. What you bring to it, it returns refined."
                multiline
              />
            </p>
          </div>

          {/* Section 2 — The Philosophy */}
          <Section label="Foundation" title={<EditableText id="about.philosophy.title" defaultValue="The Philosophy" />}>
            <p>
              <EditableText
                id="about.philosophy.p1"
                defaultValue="At the foundation of this method lies a simple recognition drawn from the non-dual tradition of Trika Shaivism:"
                multiline
                markdown
              />
            </p>
            <p className="italic text-primary/90 text-xl font-heading leading-relaxed">
              <EditableText
                id="about.philosophy.p2_spanda"
                defaultValue="The entire cosmos arose from a single, primordial pulse: Spanda, the first vibration of pure Consciousness moving to know itself. That pulse did not stop. It is still moving. It moves as the galaxies, as the seasons, as the breath in your body at this very moment."
                multiline
                markdown
              />
            </p>
            <p>
              <EditableText
                id="about.philosophy.p3_shiva_shakti"
                defaultValue="**Shiva** is the stillness, the unchanging substratum of pure awareness beneath all experience. **Shakti** is the movement, the dynamic creative intelligence through which awareness expresses itself. You are not separate from either. The body you inhabit is the precise meeting point of both."
                multiline
                markdown
              />
            </p>
            <p>
              <EditableText
                id="about.philosophy.p4"
                defaultValue="The practices are not building something new. They are dissolving what obscures what has always been present."
                multiline
                markdown
              />
            </p>
          </Section>

          {/* Section 3 — The Lineage */}
          <Section label="Source" title={<EditableText id="about.lineage.title" defaultValue="The Lineage" />}>
            <p>
              <EditableText
                id="about.lineage.p1"
                defaultValue="This method is rooted in the ancient **Kulluta Siddha** tradition of the Himalayas, one of the oldest and most complete living streams of Yogic and Tantric wisdom."
                multiline
                markdown
              />
            </p>
            <p>
              <EditableText
                id="about.lineage.p2"
                defaultValue="The Kulluta Siddhas have carried this tradition through the ages, from the Kullu valleys of Himachal Pradesh through Kashmir, Nepal, Bhutan, and into the sacred reaches of Mount Kailash and Mansarovara. For centuries this was the hidden source of Yoga and Tantra practices across the entire Himalayan belt. An unbroken line of masters preserved and transmitted these teachings selectively and in depth, stepping forward in each era when the times called for it."
                multiline
                markdown
              />
            </p>
            <p>
              <EditableText
                id="about.lineage.p3"
                defaultValue="This lineage is rooted in the living Himalayan Siddha tradition. Its oral tradition traces an unbroken lineage of masters from Lord Shiva himself to the present day, when **Mahasiddha Ishaputra** holds the seat of Kaulantak Nath, himself taught by thirty-eight Siddha Gurus collectively known as the Guru Mandala."
                multiline
                markdown
              />
            </p>
            <p>
              <EditableText
                id="about.lineage.p4"
                defaultValue="What is offered through Siddha Samaj and MyShaktiverse is a small drop from this vast ocean, shared with humility and in the spirit of genuine service to those who are sincerely seeking."
                multiline
                markdown
              />
            </p>
          </Section>

          {/* Section 4 — Siddha Dharma */}
          <Section label="Tradition" title={<EditableText id="about.dharma.title" defaultValue="Siddha Dharma" />}>
            <p>
              <EditableText
                id="about.dharma.p1"
                defaultValue="Siddha Dharma is the unbroken **guru-shishya** (teacher-disciple) tradition of the Himalayan Siddhas. Its source is Lord Shiva, understood in this tradition as the embodiment of all knowledge of this universe and the vital force that sustains it. Because Shiva and Shakti are inseparable, like the dreamer and the dream, Siddha Dharma is inherently a joint path of stillness and dynamic creative energy."
                multiline
                markdown
              />
            </p>
            <p>
              <EditableText
                id="about.dharma.p2"
                defaultValue="This lineage carries recognition at the deepest levels of the Sanatana Dharma. In the Bhagavad Gita, Shri Krishna names **Mahasiddha Kapila** among the greatest of the Siddhas, an acknowledgement that places this lineage at the very heart of this tradition."
                multiline
                markdown
              />
            </p>
            <p>
              <EditableText
                id="about.dharma.p3"
                defaultValue="The Siddhas have historically remained withdrawn from public life. In each era where spiritual understanding has declined, they have stepped forward, as Mahasiddha Matsyendranath and Gorakhnath did, to plant the seeds of this wisdom for the generations that would follow. The knowledge they transmitted encompasses Tantra, Mantra, Yantra, Ayurveda, Rasayana, and the deep inner science of sadhana, each pointing toward the same recognition: the journey of the _jeeva_ (individual) back to _Shiva_ (universal consciousness)."
                multiline
                markdown
              />
            </p>
          </Section>

          {/* Section 6 — A Note on Practice */}
          <Section label="Practice" title={<EditableText id="about.practice.title" defaultValue="A Note on Practice" />}>
            <p>
              <EditableText
                id="about.practice.p1"
                defaultValue="The Siddha Method requires regularity, sincerity, and a genuine willingness to work with yourself. The practices will meet you exactly where you are, if you show up consistently."
                multiline
              />
            </p>
            <p>
              <EditableText
                id="about.practice.p2"
                defaultValue="Progress here is quiet. It shows in the gradual reduction of internal noise, the increasing ability to remain present, and the slow but unmistakable recognition that something in you has always been still."
                multiline
              />
            </p>
          </Section>

          {/* Signature */}
          <div className="text-center space-y-4 my-12">
            <OrnateDivider variant="diamond" className="max-w-md mx-auto mb-6" />
            <p className="italic text-primary/80 text-lg leading-relaxed max-w-2xl mx-auto">
              <EditableText
                id="about.signature.line"
                defaultValue="This is a structured way to work with your own consciousness."
              />
            </p>
            <p className="font-heading text-primary tracking-wider">
              <EditableText id="about.signature.author" defaultValue="— Sanjay Ji · Siddha Samaj" />
            </p>
          </div>

          {/* CTA */}
          <div className="text-center mt-10">
            <button
              onClick={() => navigate('/courses/1')}
              className="ornate-button px-10 py-4 text-base font-heading text-primary uppercase tracking-wider rounded-lg"
              data-testid="about-start-level1"
            >
              Begin with Grahita Shakti
            </button>
          </div>
        </motion.div>
      </div>

      {/* Two-option enroll block — bottom of About page */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        <EnrollBlock onSelfPacedClick={() => navigate('/courses/1')} />
      </div>

      <WhatsAppCTA className="mt-16 border-t border-primary/20" />
    </div>
  );
};

export default About;
